<template>
  <div>
    <sweet-modal
      overlay-theme="dark"
      ref="view_task"
      enable-mobile-fullscreen
      hide-close-button
      class="no-padding-content"
      :width="modalWidth"
      @close="modalClosed"
    >
      <div class="bg-modal-gradient flex justify-between items-center p-6">
        <div class="text-white text-3xl">
          <i class="fa fa-tasks mr-4"></i> View task
        </div>
        <div>
          <button
            type="button"
            class="text-white text-2xl hover:text-grey"
            @click="closeModal"
          >
            <i class="fa fa-times"></i>
          </button>
        </div>
      </div>
      <div class="flex flex-col md:flex-row">
        <!-- file preview -->
        <div
          class="iframe-container flex-1 mr-2"
          v-if="
            task.files != undefined && task.files.length > 0 && !edit_details
          "
        >
          <div
            class="justify-center items-center h-screen flex flex-col"
            v-if="preview_loading"
          >
            <i class="fa fa-circle-notch fa-spin"></i>
            loading
          </div>
          <iframe
            :src="preview_url"
            frameborder="0"
            style="overflow: hidden; height: 100%; width: 100%"
            height="100%"
            width="100%"
            v-if="!preview_loading"
            class="iframe"
          ></iframe>
        </div>
        <!-- task view -->
        <div class="flex-1 p-6 w-full text-left" v-if="!edit_details">
          <!-- title -->
          <div class="flex flex-col-reverse md:flex-row justify-between">
            <p class="text-xl md:text-2xl font-bold">
              {{ task.title }}
            </p>
            <p class="flex self-center mb-3 md:mb-0">
              <span
                class="text-white text-sm bg-green rounded-full py-2 px-4"
                v-if="task.is_done"
              >
                <i class="fa fa-check-circle mr-2"></i> Task Completed
              </span>
              <span
                class="text-white text-sm bg-orange rounded-full py-2 px-4"
                v-else
              >
                <i class="fa fa-hourglass-half mr-2 text-xs"></i> Task Pending
              </span>
            </p>
          </div>
          <!-- details -->
          <div class="flex flex-col my-4">
            <div class="flex flex-col md:flex-row">
              <p class="text-grey-darker w-48">
                <i class="fas fa-comment mr-2"></i>Description
              </p>
              <p class="mt-2 md:mt-0">
                {{ task.description }}
              </p>
            </div>
            <div class="flex mt-4 flex-col md:flex-row">
              <p class="text-grey-darker w-48">
                <i class="fas fa-users mr-1"></i>Assignee
              </p>
              <div
                v-if="!edit_details || !is_assignee"
                class="flex mt-2 md:mt-0"
              >
                <div v-if="task.users != undefined">
                  <span v-for="(user, index) in task.users" :key="user.id">
                    {{ getUser(user.id)
                    }}<span v-if="index != task.users.length - 1">, </span>
                  </span>
                </div>
                <p v-else-if="task.pivot != undefined">
                  {{ getUser(task.pivot.entity_id) }}
                </p>
                <p v-else>Not Assigned</p>
              </div>
            </div>
            <div class="flex mt-4 flex-col md:flex-row">
              <p class="text-grey-darker w-48">
                <i class="fas fa-user mr-2"></i>Created by
              </p>
              <p class="mt-2 md:mt-0">
                {{ getUser(task.created_by_user_id) }}
                <span class="text-sm"
                  >( {{ formatDate(task.created_at, "MMM D, YYYY") }} )</span
                >
              </p>
            </div>
            <div class="flex mt-4 flex-col md:flex-row">
              <p class="text-grey-darker w-48">
                <i class="fas fa-clock mr-2"></i>Due at
              </p>
              <p>
                {{ formatDate(task.due_at, "MMM D, YYYY hh:mm A") }}
              </p>
            </div>
            <div class="flex mt-4 flex-col md:flex-row">
              <p class="text-grey-darker w-48">
                <i class="fas fa-check-circle mr-2"></i>Done at
              </p>
              <p v-if="task.done_at" class="mt-2 md:mt-0">
                {{ formatDate(task.done_at, "MMM D, YYYY hh:mm A") }}
              </p>
              <p v-else class="mt-2 md:mt-0">-</p>
            </div>
            <div class="flex mt-4">
              <div
                class="w-full text-left"
                v-if="task.files != undefined && task.files.length > 0"
              >
                <label class="text-grey-darker">
                  <i class="fas fa-file mr-3"></i>File Attached:
                </label>
                <div class="mt-2">
                  <p
                    v-for="file in task.files"
                    :key="file.id"
                    class="mb-1 flex items-center"
                  >
                    <button
                      type="button"
                      class="text-red mr-2 block"
                      v-if="task.created_by_user_id == user.id || canAccess()"
                      @click.prevent="deleteFile(file)"
                    >
                      <i class="fa fa-times-circle"></i>
                    </button>
                    <a
                      href="#"
                      @click.prevent="previewFile(file)"
                      class="text-black text-sm underline block hover:text-brand"
                      >{{ file.filename }}</a
                    >
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div class="w-full flex flex-col mt-4 mb-2" v-if="!edit_details">
            <div
              class="flex-1 flex justify-center my-4"
              v-if="!task.is_done && is_assignee"
            >
              <button
                class="bg-brand px-3 py-2 text-white rounded font-medium hover:bg-brand-darker hover:border-brand-darker"
                type="button"
                @click.prevent="markAsDone"
              >
                <i class="fa fa-check-square mr-2"></i> Mark as done
              </button>
            </div>

            <div class="flex-1">
              <task-history :histories="task.histories"></task-history>
            </div>

            <div class="flex justify-between items-center mt-auto pt-6">
              <div>
                <button
                  class="bg-brand border border-brand text-white text-sm font-medium py-2 px-4 rounded hover:bg-brand-dark hover:border-brand-dark"
                  type="button"
                  @click.prevent="editDetails"
                  v-if="is_assignee && !edit_details && role != 'userG'"
                >
                  <i class="fa fa-edit mr-2"></i> Edit
                </button>
              </div>
              <button
                class="border border-red bg-white text-red text-sm font-medium py-2 px-4 rounded hover:bg-red hover:text-white"
                type="button"
                @click.prevent="$refs.delete_task_confirm.open()"
                v-if="task.created_by_user_id == user.id || canAccess()"
              >
                <i class="fa fa-trash"></i>
              </button>
            </div>
          </div>
        </div>
        <!-- task edit -->
        <form
          @submit.prevent="updateTask()"
          data-vv-scope="edit-task"
          class="p-6 flex-1 flex flex-col justify-between"
          v-if="edit_details"
        >
          <p class="md:self-end">
            <span
              class="text-white text-sm bg-green rounded-full py-2 px-4"
              v-if="task.is_done"
            >
              <i class="fa fa-check-circle mr-2"></i> Task Completed
            </span>
            <span
              class="text-white text-sm bg-orange rounded-full py-2 px-4"
              v-else
            >
              <i class="fa fa-hourglass-half mr-2 text-xs"></i> Task Pending
            </span>
          </p>
          <div class="w-full text-left py-2 text-grey-dark text-lg flex">
            <div class="flex-1">
              <label class="font-medium text-grey-dark text-xs">Title</label>
              <input
                type="text"
                class="w-full h-10 border rounded px-3 mt-1"
                v-model="task.title"
                v-validate="'required|max:250'"
                name="task_title"
                tabindex="1"
                :class="{ 'border-red': errors.has('edit-task.task_title') }"
              />
              <div
                v-show="errors.has('edit-task.task_title')"
                class="text-red text-sm mt-1"
              >
                {{ errors.first("edit-task.task_title") }}
              </div>
            </div>
            <div class="d-block self-center"></div>
          </div>

          <div class="w-full text-left my-2">
            <label class="font-medium text-grey-dark text-xs"
              >Description</label
            >
            <textarea
              type="text"
              class="w-full border rounded py-2 px-3 mt-1"
              v-model="task.description"
              v-validate="'required'"
              name="description"
              tabindex="1"
              :class="{ 'border-red': errors.has('edit-task.description') }"
            ></textarea>
            <div
              v-show="errors.has('edit-task.description')"
              class="text-red text-sm mt-1"
            >
              {{ errors.first("edit-task.description") }}
            </div>
          </div>

          <div class="w-full text-left flex flex-col-reverse">
            <div class="flex-1 my-2">
              <label class="font-medium text-grey-dark text-xs"
                >Assigned to:</label
              >
              <form
                class="w-full flex flex-col mt-2"
                v-if="edit_details && is_assignee"
                data-vv-scope="reassign-task"
              >
                <div class="flex-1 flex justify-between">
                  <div class="flex-1">
                    <multiselect
                      v-model="task.reassign_to"
                      class="rounded"
                      :class="{
                        invalid: errors.has('reassign-task.reassign_to'),
                      }"
                      track-by="id"
                      :custom-label="name"
                      placeholder="Search a User"
                      :options="allUsers"
                      :showLabels="false"
                      name="reassign_to"
                      v-validate="'required'"
                      open-direction="below"
                      :multiple="true"
                    >
                      <template slot="tag" slot-scope="props" class="absolute">
                        <span
                          class="bg-brand-lighter text-grey-dark rounded-lg p-1 m-1"
                        >
                          <span class="font-semibold px-2"
                            >{{ props.option.first_name }}
                            {{ props.option.last_name }}</span
                          >
                          <span
                            class="text-brand border-l border-brand-dark px-2"
                            @click="props.remove(props.option)"
                          >
                            <i class="fa fa-times"></i>
                          </span>
                        </span>
                      </template>
                    </multiselect>
                  </div>
                </div>
                <div
                  v-show="errors.has('reassign-task.reassign_to')"
                  class="flex-1 text-red text-sm mt-2"
                >
                  {{ errors.first("reassign-task.reassign_to") }}
                </div>
              </form>
            </div>
            <div class="flex-1">
              <label class="font-medium text-grey-dark text-xs"
                >Due Date:</label
              >
              <div class="flex mt-2">
                <flat-pickr
                  class="appearance-none w-full h-10 border rounded px-3 text-black rounded-tr-none rounded-br-none"
                  :class="{ 'border-red': errors.has('edit-task.due_at') }"
                  placeholder="Select Date"
                  :config="flatpickr_config"
                  :events="[
                    'on-change',
                    'on-close',
                    'on-destroy',
                    'on-key-down',
                    'on-month-change',
                    'on-open',
                    'on-parse-config',
                    'on-ready',
                    'on-value-update',
                  ]"
                  v-validate
                  data-vv-rules="required|date_format:DD/MM/YYYY h:mm A"
                  data-vv-value-path="due_at"
                  data-vv-name="due_at"
                  v-model="task.due_at"
                  name="due_at"
                ></flat-pickr>
                <button
                  type="button"
                  class="text-white h-10 px-4 rounded-tr rounded-br"
                  :class="{
                    'bg-red': errors.has('edit-task.due_at'),
                    'bg-brand': !errors.has('edit-task.due_at'),
                  }"
                  data-toggle
                >
                  <i class="fa fa-calendar"></i>
                </button>
              </div>
              <div
                v-show="errors.has('edit-task.due_at')"
                class="text-red text-sm mt-1"
              >
                {{ errors.first("edit-task.due_at") }}
              </div>
            </div>
          </div>

          <div v-if="edit_details" class="my-4">
            <file-upload
              ref="fileUpload"
              v-show="!vault.storage_exceeded"
            ></file-upload>
          </div>

          <div
            class="mt-6 flex justify-between items-center"
            v-if="edit_details"
          >
            <div>
              <button
                class="bg-brand border border-brand text-white text-sm font-medium py-2 px-4 rounded hover:bg-brand-dark hover:border-brand-dark"
                type="submit"
              >
                <i class="fa fa-save mr-2"></i> Update
              </button>
              <button
                class="border border-grey bg-white text-grey-darker text-sm font-medium py-2 px-4 rounded hover:bg-grey-lightest ml-2"
                type="button"
                @click.prevent="cancelEdit"
              >
                Cancel
              </button>
            </div>
            <div>
              <button
                class="border border-red bg-white text-red text-sm font-medium py-2 px-4 rounded hover:bg-red hover:text-white"
                type="button"
                @click.prevent="$refs.delete_task_confirm.open()"
                v-if="task.created_by_user_id == user.id || canAccess()"
              >
                <i class="fa fa-trash"></i>
              </button>
            </div>
          </div>
        </form>
      </div>
    </sweet-modal>

    <confirm-dialog
      ref="delete_task_confirm"
      title="Delete this task?"
      body="This cannot be undone."
      action-label="Delete"
      @confirm="deleteTask"
    ></confirm-dialog>
  </div>
</template>

<script>
import flatPickr from "vue-flatpickr-component";
import ConfirmDialog from "../../../ConfirmDialog.vue";
import { mapGetters } from "vuex";
import FileUpload from "../FileUpload.vue";
import TaskHistory from "./History.vue";

export default {
  components: {
    ConfirmDialog,
    flatPickr,
    FileUpload,
    TaskHistory,
  },

  props: ["all-users", "selected-task", "viewer"],

  data() {
    return {
      task: {},

      preview_loading: false,
      preview_url: "",

      edit_details: false,
      is_assignee: false,

      flatpickr_config: {
        wrap: true,
        allowInput: true,
        dateFormat: "d/m/Y h:i K",
        defaultDate: new Date(),
        enableTime: true,
        monthSelectorType: "static",
        onChange: (selectedDates) => {
          if (!_.isNil(selectedDates)) {
            this.task.due_at = "";
            this.task.due_at = moment(selectedDates[0]).format(
              "DD/MM/YYYY h:mm A"
            );
          }
        },
      },
    };
  },

  methods: {
    closeModal() {
      this.$refs.view_task.close();
    },
    previewFile(file) {
      this.preview_loading = true;
      axios
        .get(`api/vaults/${file.vault_id}/files/${file.id}/download-path`, {
          params: {
            is_download: 0,
          },
        })
        .then(({ data }) => {
          this.preview_loading = false;
          if (/\.pdf$/i.test(file.filename)) {
            this.preview_url = this.viewer + encodeURIComponent(data.url);
          } else {
            let office_file_exts = [
              "doc",
              "docx",
              "dotx",
              "odt",
              "ppt",
              "pptx",
              "odp",
              "xls",
              "xlsx",
              "ods",
            ];
            let file_ext_matches = file.filename.match(/[^.]+$/);
            if (file_ext_matches.length > 0) {
              let file_ext = file_ext_matches[0];
              if (office_file_exts.indexOf(file_ext) >= 0) {
                  let baseURL = "https://docs.google.com/gview?url=";
                  if(file_ext == "doc") {
                    baseURL = "https://view.officeapps.live.com/op/embed.aspx?src=";
                  }
                // old URL: https://docs.google.com/viewer?url=
                this.preview_url =
                  baseURL +
                  encodeURIComponent(data.url) +
                  "&embedded=true&headers=true";
              } else {
                this.preview_url = data.url;
              }
            } else {
              this.preview_url = data.url;
            }
          }
        });
    },

    formatDate(date, format) {
      let d = moment(date);
      return d.format(format);
    },

    getUser(id) {
      let user = _.filter(this.allUsers, { id: id });

      if (!_.isEmpty(user)) {
        if (!user[0].last_name) {
          return user[0].first_name;
        }
        return user[0].first_name + " " + user[0].last_name;
      }

      return "-";
    },

    canAccess() {
      if (_.includes(["userA", "userB"], this.role)) {
        return true;
      }

      return false;
    },

    name(user) {
      return user.first_name + " " + user.last_name;
    },

    markAsDone() {
      axios
        .patch(`api/vaults/${this.task.vault_id}/tasks/${this.task.id}/done`)
        .then(({ data }) => {
          this.$refs.view_task.close();
          this.$emit("get-tasks");
          this.$emit("marked-as-done");
          this.$noty.success("Task has been marked as Done!", {
            killer: true,
            timeout: 1500,
          });
        })
        .catch(({ response }) => {
          if (response) {
            let data = response.data;
            if (!data.success) {
              this.$noty.error(data.message);
            }
          }
        });
    },

    deleteFile(file) {
      axios
        .delete(
          `api/vaults/${this.task.vault_id}/tasks/${this.task.id}/files/${file.id}`
        )
        .then(({ data }) => {
          this.$noty.success(data.message);
          this.task.files.splice(
            _.findIndex(this.task.files, ["id", file.id]),
            1
          );
          this.$emit("get-tasks");
        });
    },

    deleteTask() {
      this.$refs.delete_task_confirm.close();
      axios
        .delete(`api/vaults/${this.task.vault_id}/tasks/${this.task.id}`)
        .then(({ data }) => {
          if (data.success) {
            this.$noty.success(data.message, {
              killer: true,
              timeout: 1500,
            });
            this.$refs.view_task.close();
            this.$emit("get-tasks");
          }
        });
    },

    editDetails() {
      this.edit_details = true;
      // return to display format
      this.task.due_at = moment(this.task.due_at, [
        "YYYY-MM-DD HH:mm:ss",
        "DD/MM/YYYY h:mm A",
      ]).format("DD/MM/YYYY h:mm A");
    },

    cancelEdit() {
      this.edit_details = false;
      // return to display format
      this.task.due_at = moment(this.task.due_at, [
        "DD/MM/YYYY h:mm A",
        "YYYY-MM-DD HH:mm:ss",
      ]).format("YYYY-MM-DD HH:mm:ss");
    },

    updateTask() {
      this.$validator.validateAll("edit-task").then((result) => {
        if (result) {
          this.$refs.view_task.close();
          this.$emit("processing", "Updating task...");

          const files = this.$refs.fileUpload.getFiles();
          const vaultFiles = this.$refs.fileUpload.getVaultFiles();

          const formData = new FormData();
          formData.append("_method", "PATCH");
          formData.append("title", this.task.title);
          formData.append("description", this.task.description);
          formData.append(
            "due_at",
            moment(this.task.due_at, [
              "DD/MM/YYYY h:mm A",
              "YYYY-MM-DD HH:mm:ss",
            ]).format("YYYY-MM-DD HH:mm:00")
          );
          files.forEach((file, index) =>
            formData.append(`files[${index}]`, file)
          );
          vaultFiles.forEach((file, index) =>
            formData.append(`vault_files[${index}]`, file.id)
          );

          axios
            .post(
              `api/vaults/${this.task.vault_id}/tasks/${this.task.id}`,
              formData,
              {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              }
            )
            .then(({ data }) => {
              if (data.success) {
                if (this.task.reassign_to && this.task.reassign_to.length > 0) {
                  const ids = this.task.reassign_to.map((u) => u.id);
                  axios
                    .patch(
                      `api/vaults/${this.task.vault_id}/tasks/${this.task.id}/reassign`,
                      {
                        users: ids,
                      }
                    )
                    .then(({ data }) => {
                      if (data.success) {
                        _.delay(() => {
                          this.$emit("end-processing");
                          this.$emit("get-tasks", "reopen-task");
                          this.$noty.success("Task has been updated", {
                            killer: true,
                            timeout: 1500,
                          });
                        }, 1500);
                      }
                    })
                    .catch(({ response }) => {
                      if (response) {
                        let data = response.data;
                        if (!data.success) {
                          this.$noty.error(data.message);
                        }
                      }
                    });
                } else {
                  _.delay(() => {
                    this.$emit("end-processing");
                    this.$emit("get-tasks", "reopen-task");
                    this.$noty.success("Task has been updated", {
                      killer: true,
                      timeout: 1500,
                    });
                  }, 1500);
                }
              }
            })
            .catch(({ response }) => {
              this.$emit("end-processing");
              if (response) {
                let data = response.data;
                if (!data.success) {
                  this.$noty.error(data.message);
                }
              }
            });
        }
      });
    },

    modalClosed() {
      this.edit_details = false;
      this.is_assignee = false;
      this.preview_url = "";
    },

    isAssignee() {
      if (
        !_.isNil(this.task.pivot) &&
        this.user.id == this.task.pivot.entity_id
      ) {
        return true;
      }

      if (!_.isNil(this.task.users) && this.user.id == this.task.users[0].id) {
        return true;
      }

      return false;
    },
  },

  computed: {
    modalWidth() {
      if (this.$mq == "mobile") {
        return "100%";
      } else {
        if (
          this.task.files &&
          this.task.files.length > 0 &&
          !this.edit_details
        ) {
          return "80%";
        }
      }
      return "50%";
    },
    ...mapGetters({
      user: "getUser",
      role: "getUserRole",
      vault: "getCurrentVault",
    }),
  },

  watch: {
    selectedTask(val) {
      this.task = _.cloneDeep(val);
      this.is_assignee = this.isAssignee();

      if (!_.isEmpty(this.task.files)) {
        let file = _.head(this.task.files);

        this.previewFile(file);
      }
    },
  },
};
</script>

<style scoped>
.multiselect__tags {
  min-height: 0px !important;
  display: block !important;
  padding: 6px 40px 0 8px !important;
  border-radius: 5px !important;
  border: 1px solid #d8d8d8 !important;
  background: #fff !important;
}
.multiselect__input {
  font-size: 16px !important;
}
button:focus {
  outline: none;
}
</style>
